import { Box, CircularProgress } from "@mui/material";
import Addicon from "@mui/icons-material/Add";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import dateFormat from "dateformat";
import { useCallback, useMemo, useState } from "react";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import { useSelector } from "react-redux";
import { BtnPoper, Button, Text } from "$components";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import JBadge from "$components/JBadge/JBadge";
import JCombineFilters from "$components/JCombineFilters/JCombineFilters";
import JTable from "$components/JTable/JTable";
import CompanySearchInput from "$forms/CompanySearchInput";
import GenericSelect from "$forms/GenericSelect";
import WarehouseSearchInput from "$forms/WarehouseSearchInput";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { STATUS_ORDERS } from "$hooks/useOrder";
import {
  Order,
  OrderStatus,
  ORDER_STATUS_LABELS,
  ORDER_STATE_LABELS,
  getOrderState,
} from "$types";
import { DEFAULT_DATE_FORMAT, isAnObjectId } from "$utils/utils";
import { OrderRowSubComponent } from "./OrderRowSubComponent";
import { appState } from "$reducers";

interface OrdersTabProps {
  setSelectedOrder: (order: any) => void;
  selectedRowsOrder: any[];
  setSelectedRows: (rows: any) => void;
  setShowImportModal: (showImportModal: boolean) => void;
  setShowImportUpdateModal: (showImportUpdateModal: boolean) => void;
  setShowImportUpdateAllModal: (showImportUpdateAllModal: boolean) => void;
  setPopinOpen: (isOpen: boolean) => void;
  isTransfer: boolean;
  filteredOrders: any[];
  warehouses: any[];
  exportSelectedOrders: (commandAdded: boolean) => void;
  companiesSupplier: any[];
  companiesClient: any[];
  isFetching: boolean;
  setFilter: (key: string, value: any) => void;
  setPageIndex: (nextPage: number) => void;
  pageIndex: number;
  pageCount: number;
  filters: any;
  togglePopin: any;
  getNewOrderType: any;
  isExporting: boolean;
  dockyardsMap: Map<string, any>;
}

function OrdersTab({
  setSelectedOrder,
  selectedRowsOrder,
  setSelectedRows,
  setShowImportModal,
  setShowImportUpdateModal,
  setShowImportUpdateAllModal,
  setPopinOpen,
  isTransfer,
  filteredOrders,
  warehouses,
  exportSelectedOrders,
  companiesSupplier,
  companiesClient,
  isFetching,
  setFilter,
  setPageIndex,
  pageIndex,
  pageCount,
  filters,
  togglePopin,
  getNewOrderType,
  isExporting,
  dockyardsMap,
}: OrdersTabProps) {
  const { isAdmin, isClient } = useAuth();
  const { jclasses } = useJStyles();
  const { filter: sidebarCompanyFilter } = useSelector(appState);
  const commonClasses = useJStyles().jclasses;

  // State for delivery date filter
  const [deliveryDateStart_tfilter, setDeliveryDateStart_tfilter] =
    useState(null);
  const [deliveryDateEnd_tfilter, setDeliveryDateEnd_tfilter] = useState(null);

  const getOrderCompany = useCallback(
    (order: Order) => {
      return companiesClient?.find((c) => c._id === order?.company);
    },
    [companiesClient]
  );

  const onDeliveryDateChange = useCallback(
    (dates: any) => {
      if (Array.isArray(dates)) {
        const [start, end] = dates;
        setDeliveryDateStart_tfilter(start);
        setDeliveryDateEnd_tfilter(end);
        if (!start && !end) {
          setDeliveryDateStart_tfilter(null);
          setDeliveryDateEnd_tfilter(null);
          setFilter("deliveryDate", undefined);
          setFilter("deliveryDate][$gte", undefined);
          setFilter("deliveryDate][$lte", undefined);
        } else if (start === end || !end) {
          setFilter("deliveryDate", dateFormat(start, "yyyy-mm-dd"));
        } else {
          setFilter("deliveryDate", undefined);
          setFilter("deliveryDate][$gte", dateFormat(start, "yyyy-mm-dd"));
          setFilter("deliveryDate][$lte", dateFormat(end, "yyyy-mm-dd"));
        }
      }
    },
    [setDeliveryDateEnd_tfilter, setDeliveryDateStart_tfilter, setFilter]
  );

  const getProviderName = useCallback(
    (provider: string, warehouseOrigin: any) => {
      if (isTransfer) {
        return (
          warehouses?.find((entrepot) => warehouseOrigin === entrepot._id)
            ?.name || "N/A"
        );
      }
      if (isAnObjectId(provider)) {
        return companiesSupplier?.find(({ _id }) => provider === _id)?.name;
      }
      return provider;
    },
    [companiesSupplier, isTransfer, warehouses]
  );

  const getWarehouseName = useCallback(
    (warehouseId: string) => {
      return warehouses?.find((w) => warehouseId === w._id)?.name || "N/A";
    },
    [warehouses]
  );

  // @todo: create a component for clickable cell and use it everywhere
  // should come after the refacto of our current tables
  const columns = useMemo(() => {
    return [
      {
        Header: "Entreprise",
        filterName: "__company",
        filterComponent: (props: any) => {
          return (
            <CompanySearchInput
              value={props.value || sidebarCompanyFilter?.name}
              disabled={!!sidebarCompanyFilter?.name}
              label=""
              onChange={(val, ids) => {
                if (sidebarCompanyFilter?._id) {
                  setFilter("company", { $eq: sidebarCompanyFilter._id });
                } else if (!!val && ids?.length) {
                  setFilter("company", { $in: ids });
                } else {
                  setFilter("company", undefined);
                }
              }}
              orderModule
            />
          );
        },
        maxWidth: 180,
        minWidth: 180,
        Cell: ({ row: { original } }: any) => {
          const company = getOrderCompany(original);
          const orderCompanyName = company?.name || "N/A";
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text
                type={company?.isActive ? "important" : "disabled"}
                style={{
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {orderCompanyName}
              </Text>
            </div>
          );
        },
      },
      {
        Header: "Date livraison estimée",
        minWidth: 130,
        maxWidth: 130,
        filterName: "deliveryDate",
        filterComponent: () => (
          <BataillogDatePicker
            selectsRange
            startDate={deliveryDateStart_tfilter}
            endDate={deliveryDateEnd_tfilter}
            onDateChange={onDeliveryDateChange}
          />
        ),
        Cell: ({ row: { original } }: any) => {
          const { deliveryDate } = original;
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {deliveryDate
                  ? dateFormat(deliveryDate, DEFAULT_DATE_FORMAT)
                  : "N/A"}
              </Text>
            </div>
          );
        },
      },
      {
        Header: isTransfer ? "Fournisseur / Entrepôt d'origine" : "Fournisseur",
        filterName: "__provider",
        filterComponent: (props: any) => (
          <CompanySearchInput
            value={props.value}
            label=""
            onChange={(val, ids) => {
              if (!val) {
                setFilter("provider", undefined);
                return;
              }
              const value = ids.length ? ([...ids, val] as any) : [val];
              setFilter("provider", { $in: value });
            }}
            orderModule
          />
        ),
        Cell: ({ row: { original } }: any) => {
          const { provider, warehouseOrigin } = original;
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text
                type={company?.isActive ? "text" : "disabled"}
                onClick={() => {
                  setSelectedOrder(original);
                  setPopinOpen(true);
                }}
              >
                {getProviderName(provider, warehouseOrigin)}
              </Text>
            </div>
          );
        },
      },
      {
        Header: isTransfer ? "Entrepôt de réception" : "Entrepôt",
        filterName: "__warehouse",
        minWidth: 90,
        filterComponent: (props: any) => (
          <WarehouseSearchInput
            value={props.value}
            label=""
            onChange={(val, ids) => {
              props.onChange(val);
              if (ids && ids.length > 0) {
                setFilter("warehouse", { $in: ids });
              } else {
                setFilter("warehouse", undefined);
              }
            }}
            orderModule
          />
        ),
        Cell: ({ row: { original } }: any) => {
          const { warehouse } = original;
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {getWarehouseName(warehouse)}
              </Text>
            </div>
          );
        },
      },
      {
        Header: "N° de commande",
        filterName: "reference",
        maxWidth: 120,
        minWidth: 120,
        Cell: ({ row: { original } }: any) => {
          const { reference } = original;
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {reference}
              </Text>
            </div>
          );
        },
      },
      ...(!isTransfer
        ? [
            {
              Header: () => <>Type</>,
              accessor: "providerType",
              maxWidth: 90,
              minWidth: 90,
              filterName: "providerType",
              filterComponent: (props: any) => (
                <GenericSelect
                  className={jclasses.jTableLabelHeader}
                  onChange={(val) => {
                    props.onChange(val);
                  }}
                  value={filters.providerType || props.value}
                  options={[
                    { value: "provider", label: "Fournisseur" },
                    { value: "reverse", label: "Reverse" },
                  ]}
                />
              ),
              Cell: ({ row: { original } }: any) => {
                const { providerType } = original;
                const company = getOrderCompany(original);
                return (
                  <div
                    onClick={() => {
                      setSelectedOrder(original);
                      setPopinOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-hidden="true"
                    className={jclasses.tableClickableDiv}
                  >
                    {providerType !== null ? (
                      <Text type={company?.isActive ? "text" : "disabled"}>
                        {providerType === "provider"
                          ? "Fournisseur"
                          : providerType === "reverse"
                            ? "Reverse"
                            : "Transfert"}
                      </Text>
                    ) : (
                      <Text type={company?.isActive ? "text" : "disabled"}>
                        Aucun
                      </Text>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),

      {
        Header: () => (
          <>
            Type
            <br />
            appro
          </>
        ),
        accessor: "type",
        maxWidth: 90,
        minWidth: 90,
        filterName: "type",
        filterComponent: (props: any) => (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
            }}
            value={filters.type ?? props.value}
            options={[
              { value: "stock", label: "Stock" },
              { value: "dockyard", label: "Chantier" },
              { value: "project", label: "Projet" },
              {
                value: [
                  "dockyard,stock",
                  "dockyard,project",
                  "dockyard,stock,project",
                  "dockyard,project,stock",
                  "stock,dockyard",
                  "stock,project",
                  "stock,dockyard,project",
                  "stock,project,dockyard",
                  "project,dockyard",
                  "project,stock",
                  "project,stock,dockyard",
                  "project,dockyard,stock",
                ],
                label: "Multiple",
              },
            ]}
          />
        ),
        Cell: ({ row: { original } }: any) => {
          const { type } = original;
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
            >
              <Text type={company?.isActive ? "text" : "disabled"}>
                {getNewOrderType(type)}
              </Text>
            </div>
          );
        },
      },
      {
        Header: "Statut livraison",
        filterName: "status",
        minWidth: 100,
        maxWidth: 100,
        filterComponent: (props: any) => (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
            }}
            value={props.value}
            options={[
              {
                value: OrderStatus.TO_DELIVERED,
                label: ORDER_STATUS_LABELS[OrderStatus.TO_DELIVERED],
              },
              {
                value: OrderStatus.DELIVERED_INCOMPLETE,
                label: ORDER_STATUS_LABELS[OrderStatus.DELIVERED_INCOMPLETE],
              },
              {
                value: OrderStatus.DELIVERED_OK,
                label: ORDER_STATUS_LABELS[OrderStatus.DELIVERED_OK],
              },
              {
                value: OrderStatus.CANCELED,
                label: ORDER_STATUS_LABELS[OrderStatus.CANCELED],
              },
            ]}
            multiple
          />
        ),
        Cell: ({ row: { original } }: any) => {
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <JBadge
                variant={original?.status}
                messages={STATUS_ORDERS}
                disabled={!company?.isActive}
              />
            </div>
          );
        },
      },
      {
        Header: "Statut Commande",
        minWidth: 100,
        maxWidth: 100,
        Cell: ({ row: { original } }: any) => {
          const company = getOrderCompany(original);
          return (
            <div
              onClick={() => {
                setSelectedOrder(original);
                setPopinOpen(true);
              }}
              role="button"
              tabIndex={0}
              aria-hidden="true"
              className={jclasses.tableClickableDiv}
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <JBadge
                variant={getOrderState(original?.status, original?.closed)}
                messages={ORDER_STATE_LABELS as any}
                disabled={!company?.isActive}
              />
            </div>
          );
        },
      },
      {
        Header: "Conformité",
        filterName: "conformity",
        minWidth: 90,
        maxWidth: 90,
        filterComponent: (props: any) => (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
            }}
            value={props.value}
            options={[
              { value: "true", label: "Conforme" },
              { value: "false", label: "Non conforme" },
            ]}
          />
        ),
        Cell: ({
          row: {
            original,
            original: { conformity },
          },
        }: any) => (
          <div
            onClick={() => {
              setSelectedOrder(original);
              setPopinOpen(true);
            }}
            role="button"
            tabIndex={0}
            aria-hidden="true"
            className={jclasses.tableClickableDiv}
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {conformity === null ? null : conformity === true ? (
              <SentimentSatisfiedAltIcon style={{ color: "green" }} />
            ) : (
              <SentimentVeryDissatisfiedIcon style={{ color: "tomato" }} />
            )}
          </div>
        ),
      },
      {
        Header: null,
        accessor: "expander",
        minWidth: 60,
        maxWidth: 60,
        preventClickAction: true,
        Cell: ({
          row,
          row: {
            original: { suborders },
          },
        }: any) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {suborders?.length > 1 ? (
              <span
                onClick={row.getToggleExpandedHandler()}
                style={{
                  minWidth: 30,
                  padding: 3,
                  marginLeft: 4,
                  position: "relative",
                  top: 2,
                }}
              >
                {row.getIsExpanded() ? (
                  <ArrowUpSLineIcon />
                ) : (
                  <ArrowDownSLineIcon />
                )}
              </span>
            ) : null}
          </div>
        ),
      },
    ];
  }, [
    jclasses.tableClickableDiv,
    isTransfer,
    setFilter,
    getOrderCompany,
    setSelectedOrder,
    setPopinOpen,
    deliveryDateStart_tfilter,
    deliveryDateEnd_tfilter,
    onDeliveryDateChange,
    getProviderName,
    jclasses.jTableLabelHeader,
    filters.providerType,
    getNewOrderType,
    getWarehouseName,
    sidebarCompanyFilter,
    filters.type,
  ]);

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const subItems = row.original.suborders;
      const parentOrder = row.original;
      const subColumns = [
        {
          Header: "Référence",
          id: "reference",
          Cell: ({
            row: {
              original: {
                reference,
                type,
                suborderProducts,
                dockyard,
                project,
              },
            },
          }: any) => (
            <OrderRowSubComponent
              reference={reference}
              type={type}
              suborderProducts={suborderProducts}
              dockyardId={dockyard}
              dockyard={dockyardsMap.get(dockyard)}
              projectId={project}
            />
          ),
        },
      ];
      return row.original.suborders.length > 1 ? (
        <JTable
          data={subItems ?? []}
          columns={subColumns}
          isFetching={isFetching}
          pageCount={pageCount}
          filters={filters}
          setFilter={setFilter}
          setPageIndex={setPageIndex}
          pageIndex={pageIndex}
          isSubTable
          hideCheckboxes={!isAdmin()}
          onClickAction={() => {
            togglePopin(true);
            setSelectedOrder(parentOrder);
          }}
        />
      ) : null;
    },
    [
      isFetching,
      pageCount,
      filters,
      setFilter,
      setPageIndex,
      pageIndex,
      togglePopin,
      setSelectedOrder,
      isAdmin,
      dockyardsMap,
    ]
  );

  return (
    <div style={{ marginTop: 15 }}>
      <div className={jclasses.jBeforePaper}>
        <div className={jclasses.jBeforePaper_filter}>
          <JCombineFilters
            isOrdersTab
            currentCompany={filters?.company?.$eq}
            onChange={(data: any) =>
              setFilter("global", data != null ? { $eq: data } : undefined)
            }
            value={filters?.global?.$eq}
          />

          {(isAdmin() || isClient()) && (
            <div
              style={{
                marginLeft: 8,
                marginRight: 8,
                display: "flex",
                alignItems: "center",
                zIndex: 3,
              }}
            >
              {isAdmin() && (
                <Button
                  className={jclasses.jAddCta}
                  variant="outlined"
                  onClick={() => {
                    togglePopin(true);
                    setPopinOpen(true);
                    setSelectedOrder(null);
                  }}
                >
                  <Addicon />
                  Ajouter une commande
                </Button>
              )}

              <Button
                variant="outlined"
                onClick={() => {
                  exportSelectedOrders(selectedRowsOrder?.length === 0);
                }}
                disabled={isExporting}
              >
                {isExporting ? (
                  <CircularProgress
                    size={16}
                    className={commonClasses.loaderIcon}
                  />
                ) : (
                  <Upload2LineIcon />
                )}
                {`${
                  selectedRowsOrder?.length === 0
                    ? "Exporter toutes les commandes"
                    : `Exporter des commandes (${selectedRowsOrder?.length})`
                }`}
              </Button>

              {isAdmin() && (
                <BtnPoper
                  BtnLabel="Opérations en masse"
                  BtnComponent={Button}
                  BtnProps={{
                    variant: "outlined",
                  }}
                  BtnIcon={ExpandMoreIcon}
                  className={jclasses.jAddCta2}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: "300px",
                    }}
                  >
                    {!isTransfer && (
                      <Button
                        onClick={() => {
                          setShowImportModal(true);
                        }}
                        variant="contained"
                        className={jclasses.jBtnOnPopper}
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          borderRadius: 0,
                          color: "black !important",
                          backgroundColor: "transparent !important",
                          border: "none !important",
                          padding: "6px",
                          minWidth: "300px",
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                            color: "black !important",
                            border: "none !important",
                          },
                          "&.MuiButton-text:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                          "&.MuiButton-root:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                          "& svg": {
                            color: "black !important",
                            marginRight: "8px",
                          },
                          "& .MuiButton-label": {
                            color: "black !important",
                          },
                          "& span": {
                            color: "black !important",
                          },
                        }}
                      >
                        <Download2LineIcon />
                        Importer des commandes
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setShowImportUpdateAllModal(true);
                      }}
                      variant="contained"
                      className={jclasses.jBtnOnPopper}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        borderRadius: 0,
                        color: "black !important",
                        backgroundColor: "transparent !important",
                        border: "none !important",
                        padding: "6px",
                        minWidth: "300px",
                        "&:hover": {
                          backgroundColor: "#e0e0e0 !important",
                          color: "black !important",
                          border: "none !important",
                        },
                        "&.MuiButton-text:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                        "&.MuiButton-root:hover": {
                          backgroundColor: "#e0e0e0 !important",
                        },
                        "& svg": {
                          color: "black !important",
                          marginRight: "8px",
                        },
                        "& .MuiButton-label": {
                          color: "black !important",
                        },
                        "& span": {
                          color: "black !important",
                        },
                      }}
                    >
                      <Edit2LineIcon />
                      Mettre à jour toutes les commandes
                    </Button>
                    {selectedRowsOrder?.length > 0 && (
                      <Button
                        onClick={() => {
                          setShowImportUpdateModal(true);
                        }}
                        variant="contained"
                        className={jclasses.jBtnOnPopper}
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          borderRadius: 0,
                          color: "black !important",
                          backgroundColor: "transparent !important",
                          border: "none !important",
                          padding: "6px",
                          minWidth: "300px",
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                            color: "black !important",
                            border: "none !important",
                          },
                          "&.MuiButton-text:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                          "&.MuiButton-root:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                          "& svg": {
                            color: "black !important",
                            marginRight: "8px",
                          },
                          "& .MuiButton-label": {
                            color: "black !important",
                          },
                          "& span": {
                            color: "black !important",
                          },
                        }}
                      >
                        <Edit2LineIcon />
                        Mettre à jour des commandes ({selectedRowsOrder?.length}
                        )
                      </Button>
                    )}
                  </Box>
                </BtnPoper>
              )}
            </div>
          )}
        </div>
      </div>

      <Box>
        <Text>
          Commande(s) sélectionnée(s) : {selectedRowsOrder?.length || 0}
        </Text>{" "}
        {selectedRowsOrder?.length > 0 && (
          <button
            type="button"
            onClick={() => setSelectedRows([])}
            className={jclasses.ghostLink}
            style={{
              marginTop: 4,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#457DD9",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            (Effacer la sélection)
          </button>
        )}
      </Box>

      <JTable
        hideCheckboxes={!isAdmin() && !isClient()}
        data={filteredOrders || []}
        columns={columns}
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        renderRowSubComponent={renderRowSubComponent}
        onClickAction={() => {
          togglePopin(true);
        }}
        setSelectedItem={setSelectedOrder}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRowsOrder}
        pageName="orderList"
        noDataText="Aucune commande n'a encore été créée"
      />
    </div>
  );
}

export default OrdersTab;
